// src/components/artwork/FieldProvenance.jsx
// §12/§16 — Traçabilité documentaire par champ. Affiche, pour les champs
// alimentés depuis un document importé, leur SOURCE (et statut). « Transforme
// l'OCR en traçabilité documentaire. »
import React from "react";
import { useTranslation } from "react-i18next";
import { FileText } from "lucide-react";

const FIELD_LABELS = {
  fr: { title: "Titre", artist: "Artiste", year: "Année", medium: "Technique", dimensions: "Dimensions",
    acquisition_type: "Type d'acquisition", acquisition_date: "Date d'acquisition", acquisition_price: "Prix", currency: "Devise",
    auction_house: "Maison de vente", auction_lot_number: "N° de lot" },
  en: { title: "Title", artist: "Artist", year: "Year", medium: "Medium", dimensions: "Dimensions",
    acquisition_type: "Acquisition type", acquisition_date: "Acquisition date", acquisition_price: "Price", currency: "Currency",
    auction_house: "Auction house", auction_lot_number: "Lot no." },
  de: { title: "Titel", artist: "Künstler", year: "Jahr", medium: "Technik", dimensions: "Maße",
    acquisition_type: "Erwerbsart", acquisition_date: "Erwerbsdatum", acquisition_price: "Preis", currency: "Währung",
    auction_house: "Auktionshaus", auction_lot_number: "Losnr." },
};
const STATUS_LABELS = {
  fr: { extracted: "Extrait d'un document", proposed: "Proposé par l'IA", confirmed: "Confirmé", expert: "Validé par un expert" },
  en: { extracted: "Extracted from a document", proposed: "Proposed by AI", confirmed: "Confirmed", expert: "Expert-validated" },
  de: { extracted: "Aus Dokument extrahiert", proposed: "Von KI vorgeschlagen", confirmed: "Bestätigt", expert: "Von Experten bestätigt" },
};
const COPY = {
  fr: { title: "Sources documentaires", hint: "D'où proviennent les informations importées." },
  en: { title: "Documentary sources", hint: "Where the imported information comes from." },
  de: { title: "Dokumentenquellen", hint: "Woher die importierten Informationen stammen." },
};
const pick = (o, l) => o[String(l || "fr").slice(0, 2).toLowerCase()] || o.fr;

export default function FieldProvenance({ artwork }) {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;
  const fl = pick(FIELD_LABELS, lang);
  const sl = pick(STATUS_LABELS, lang);
  const c = pick(COPY, lang);

  let prov = artwork?.field_provenance;
  if (typeof prov === "string") { try { prov = JSON.parse(prov); } catch { prov = null; } }
  const entries = prov && typeof prov === "object" ? Object.entries(prov).filter(([, v]) => v && v.source) : [];
  if (!entries.length) return null;

  return (
    <div style={{ border: "1px solid #e8e4de", borderRadius: 8, overflow: "hidden", marginBottom: 16, fontFamily: "system-ui, sans-serif" }}>
      <div style={{ background: "#f9f7f4", padding: "9px 14px" }}>
        <div style={{ fontSize: 11, color: "#8b7355", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{c.title}</div>
        <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>{c.hint}</div>
      </div>
      <div>
        {entries.map(([key, v], i) => (
          <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 14px", borderTop: i > 0 ? "1px solid #f0ede8" : "none" }}>
            <span style={{ fontSize: 13, color: "#1a1a1a" }}>{fl[key] || key}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6b6b6b", minWidth: 0 }}>
              <FileText size={12} color="#8b7355" style={{ flexShrink: 0 }} />
              <span style={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={v.source}>{v.source}</span>
              <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 999, background: "#f1f6f1", color: "#2c5f2d", flexShrink: 0 }}>{sl[v.status] || sl.extracted}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
